/**
 * Maps wizard config <-> Keeper /v1/bls multipart fields.
 *
 * Question copy, answers, click-to-submit, selected Flight IDs, and frequency
 * buckets are not all first-class columns. Edit setup hydrates those from the
 * saved survey HTML plus Vuex/localStorage extras.
 */

import { BLS_MODE, BLS_STATUS, SURVEY_SOURCE, createDefaultWizardConfig, daysToSeconds, durationToDays } from './constants.js';
import { setIdsForParentIds, uniqueParentIdsForSets } from './flights.js';
import { getMetricById, inferActionWord, defaultAnswersForMetric, buildQuestion, reconcileSavedAnswers } from './metrics.js';
import { buildTraffickedSurveyHtml, parseSurveyHtml, surveyHtmlToFile } from './surveyTemplate.js';

function orderedAnswers(config) {
  const answers = (config.answerOptions || []).map((value) => String(value || '').trim()).filter(Boolean);
  const positiveIndex = Number(config.positiveAnswerIndex) || 0;
  if (!answers.length) return [];
  if (positiveIndex <= 0 || positiveIndex >= answers.length) return answers;
  const positive = answers[positiveIndex];
  return [positive, ...answers.filter((_, index) => index !== positiveIndex)];
}

export function wizardConfigToFormData(config, sets = [], { includeHtml = true } = {}) {
  const form = new FormData();
  const isOptimized = config.blsMode === BLS_MODE.OPTIMIZED;
  const answers = orderedAnswers(config);
  const metric = getMetricById(config.brandMetric);

  form.append('campaign_id', config.campaignId || '');
  form.append('name', config.name || 'Brand Lift Survey');
  form.append('brand', config.brand || '');
  form.append('metric', metric?.label || config.brandMetric || 'Custom');
  form.append('audience_percentage', String(Math.min(50, Math.max(0, Number(config.controlSplit) || 0))));
  form.append(
    'n_exposures',
    String(config.exposureFrequency || 1)
  );
  form.append('survey_ask_limit', String(config.askLimit || 1));
  form.append('days_display_window', String(durationToDays(config.displayWindowMax, config.displayWindowMaxUnit)));
  form.append('survey_re_ask_impressions_interval', String(config.returnFrequency || 1));
  form.append('optimized', isOptimized ? '1' : '0');
  form.append('brand_metric_question', config.questionWording || '');
  form.append('answers', JSON.stringify(answers));
  form.append('click_to_submit', '0');

  const parentIds = isOptimized ? uniqueParentIdsForSets(sets, config.selectedSetIds) : [];
  form.append('creative_groups', JSON.stringify(parentIds));
  form.append('minimum_data_sample_pool', String(isOptimized ? config.minTestVolume || 0 : 0));
  form.append('minimum_performance_lead', String(isOptimized ? config.minPerformanceLead || 0 : 0));
  const periodDays = isOptimized
    ? (config.minTestPeriodUnit === 'weeks'
      ? (Number(config.minTestPeriod) || 0) * 7
      : Number(config.minTestPeriod) || 0)
    : 0;
  form.append('minimum_test_period', String(daysToSeconds(periodDays)));
  form.append('both_minimums', isOptimized && config.startOptimizationWhen === 'both' ? '1' : '0');
  form.append('always_on', isOptimized && (config.flightPromotion === 'always-on') ? '1' : '0');
  form.append('n_promotions', String(isOptimized ? Number(config.performersToPromote) : 1));
  form.append(
    'top_performer_allocation',
    String(isOptimized ? Math.min(99, Math.max(1, Number(config.winningInventoryPercent) || 80)) : 80)
  );

  if (includeHtml && config.htmlFile) {
    const filename = config.htmlFileName || config.htmlFile.name || 'survey.html';
    form.append('html', config.htmlFile, filename);
  }

  return form;
}

export async function wizardConfigToFormDataAsync(config, sets = [], { existingHtml } = {}) {
  let uploadedHtml = existingHtml || null;
  if (!uploadedHtml && config.surveySource === 'upload' && config.htmlFile && typeof config.htmlFile.text === 'function') {
    uploadedHtml = await config.htmlFile.text();
  }
  const html = buildTraffickedSurveyHtml({ ...config, clickToSubmit: false }, uploadedHtml);
  const htmlFile = surveyHtmlToFile(html, config);
  return wizardConfigToFormData({ ...config, htmlFile, htmlFileName: htmlFile.name }, sets);
}

export function normalizeCreativeGroups(creativeGroups) {
  if (!creativeGroups) return [];
  if (Array.isArray(creativeGroups)) return creativeGroups.map(String);
  if (Array.isArray(creativeGroups.groups)) return creativeGroups.groups.map(String);
  if (typeof creativeGroups === 'object') return Object.keys(creativeGroups);
  return [];
}

export function entityToWizardConfig(entity, extras = {}, campaign = {}, options = {}) {
  const parsedHtml = options.parsedHtml || parseSurveyHtml(options.html || '');
  const sets = options.sets || [];
  const groups = normalizeCreativeGroups(entity?.creativeGroups);
  const defaults = createDefaultWizardConfig(campaign);
  const metricId = extras.brandMetric || metricIdFromLabel(entity?.metric) || '';
  const metric = getMetricById(metricId);
  const brand = coerceText(entity?.brand, extras.brand, campaign.brand, defaults.brand) || '';
  const actionWord = extras.questionActionWord || inferActionWord(metric, coerceText(parsedHtml.questionWording, extras.questionWording) || '');
  const catalogueQuestion = metric && metric.id !== 'custom' ? buildQuestion(metric, actionWord, brand) : '';
  const questionWording = catalogueQuestion || coerceText(
    parsedHtml.questionWording,
    extras.questionWording,
    defaults.questionWording
  ) || '';
  const parsedAnswers = parsedHtml.answerOptions?.length
    ? parsedHtml.answerOptions
    : extras.answerOptions?.length
      ? extras.answerOptions
      : [];
  const reconciled = metric
    ? reconcileSavedAnswers(metric, parsedAnswers, brand, extras.answerCount)
    : { answerCount: defaults.answerCount, answerOptions: defaults.answerOptions };
  const answerCount = reconciled.answerCount || defaults.answerCount;
  const answerOptions = reconciled.answerOptions?.length
    ? reconciled.answerOptions
    : (metric ? defaultAnswersForMetric(metric, answerCount, brand) : defaults.answerOptions) || [];
  const positiveText = coerceText(entity?.positiveAnswer, extras.positiveAnswer);
  const positiveFromAnswers = positiveText
    ? answerOptions.findIndex((answer) => String(answer) === String(positiveText))
    : -1;
  const optimizedFlag = coerceBool(entity?.optimized, extras.optimized);
  const blsMode = extras.blsMode
    || (optimizedFlag === true || groups.length > 0 ? BLS_MODE.OPTIMIZED : BLS_MODE.STANDARD);
  const selectedParentIds = extras.selectedParentIds?.length ? extras.selectedParentIds : groups;
  const selectedSetIds = extras.selectedSetIds?.length
    ? extras.selectedSetIds
    : setIdsForParentIds(sets, selectedParentIds);
  const surveySource = SURVEY_SOURCE.BUILD;
  const period = periodFromEntity(entity, extras, defaults);
  const minLead = coerceNumber(extras.minPerformanceLead, entity?.minimumPerformanceLead, defaults.minPerformanceLead);
  const bothMinimums = coerceBool(entity?.bothMinimums, extras.bothMinimums);
  const alwaysOn = coerceBool(entity?.alwaysOn, extras.alwaysOn);

  return {
    ...defaults,
    ...extras,
    id: entity?.id || extras.id || null,
    status: entity?.status || extras.status || BLS_STATUS.DRAFT,
    name: coerceText(entity?.name, extras.name, defaults.name) || defaults.name,
    brand,
    campaignId: entity?.campaignId || extras.campaignId || campaign.id || null,
    campaignName: extras.campaignName || campaign.name || defaults.campaignName,
    surveySource,
    brandMetric: metricId,
    questionActionWord: actionWord,
    questionWording,
    answerCount,
    answerOptions,
    positiveAnswerIndex: extras.positiveAnswerIndex ?? (positiveFromAnswers >= 0 ? positiveFromAnswers : 0),
    clickToSubmit: false,
    htmlFileName: extras.htmlFileName || '',
    controlSplit: coerceNumber(
      entity?.audiencePercentage,
      entity?.audience_percentage,
      extras.controlSplit,
      defaults.controlSplit
    ) ?? defaults.controlSplit,
    exposureFrequency: coerceNumber(entity?.nExposures, extras.exposureFrequency, defaults.exposureFrequency)
      ?? defaults.exposureFrequency,
    askLimit: coerceNumber(entity?.surveyAskLimit, extras.askLimit, defaults.askLimit) ?? defaults.askLimit,
    returnFrequency: coerceNumber(
      entity?.surveyReAskImpressionsInterval,
      extras.returnFrequency,
      defaults.returnFrequency
    ) ?? defaults.returnFrequency,
    displayWindowMax: extras.displayWindowMax ?? coerceNumber(entity?.daysDisplayWindow, defaults.displayWindowMax)
      ?? defaults.displayWindowMax,
    displayWindowMaxUnit: extras.displayWindowMaxUnit || defaults.displayWindowMaxUnit,
    blsMode,
    selectedParentIds,
    selectedSetIds,
    minPerformanceLead: minLead ?? defaults.minPerformanceLead,
    minTestPeriod: period.minTestPeriod,
    minTestPeriodUnit: period.minTestPeriodUnit,
    minTestVolume: coerceNumber(entity?.minimumDataSamplePool, extras.minTestVolume, defaults.minTestVolume)
      ?? defaults.minTestVolume,
    startOptimizationWhen: extras.startOptimizationWhen
      || (bothMinimums === false ? 'either' : defaults.startOptimizationWhen),
    flightPromotion: extras.flightPromotion
      || (alwaysOn === false ? 'full' : defaults.flightPromotion),
    performersToPromote: coerceNumber(
      entity?.nPromotions,
      entity?.performersToPromote,
      extras.performersToPromote,
      defaults.performersToPromote
    ) ?? defaults.performersToPromote,
    winningInventoryPercent: coerceNumber(
      entity?.topPerformerAllocation,
      entity?.winningInventoryPercent,
      extras.winningInventoryPercent,
      defaults.winningInventoryPercent
    ) ?? defaults.winningInventoryPercent,
    createdAt: entity?.createdAt || extras.createdAt,
    updatedAt: entity?.updatedAt || extras.updatedAt,
    creativeId: entity?.creativeId || extras.creativeId,
    frequencyTesting: {
      ...(defaults.frequencyTesting || {}),
      ...(extras.frequencyTesting || {}),
      enabled: false,
      buckets: [...(extras.frequencyTesting?.buckets || defaults.frequencyTesting.buckets || [])],
    },
    forecast: {
      ...(defaults.forecast || {}),
      ...(extras.forecast || {}),
      failedSetIds: [...(extras.forecast?.failedSetIds || [])],
    },
  };
}

function coerceText(...values) {
  for (const value of values) {
    if (value === undefined || value === null) continue;
    const text = String(value);
    if (text !== '') return text;
  }
  return undefined;
}

function coerceNumber(...values) {
  for (const value of values) {
    if (value === undefined || value === null || value === '') continue;
    const number = Number(value);
    if (!Number.isNaN(number)) return number;
  }
  return undefined;
}

function coerceBool(...values) {
  for (const value of values) {
    if (value === undefined || value === null || value === '') continue;
    if (value === true || value === 1 || value === '1') return true;
    if (value === false || value === 0 || value === '0') return false;
  }
  return undefined;
}

function periodFromEntity(entity, extras, defaults) {
  if (extras.minTestPeriod != null && extras.minTestPeriod !== '') {
    return {
      minTestPeriod: Number(extras.minTestPeriod) || defaults.minTestPeriod,
      minTestPeriodUnit: extras.minTestPeriodUnit || defaults.minTestPeriodUnit,
    };
  }
  const seconds = coerceNumber(entity?.minimumTestPeriod);
  if (seconds == null) {
    return {
      minTestPeriod: defaults.minTestPeriod,
      minTestPeriodUnit: defaults.minTestPeriodUnit,
    };
  }
  const days = Math.max(0, Math.round(seconds / 86400));
  if (days >= 14 && days % 7 === 0) {
    return { minTestPeriod: days / 7, minTestPeriodUnit: 'weeks' };
  }
  return { minTestPeriod: days || defaults.minTestPeriod, minTestPeriodUnit: 'days' };
}

function metricIdFromLabel(label) {
  if (!label) return '';
  const normalised = String(label).toLowerCase();
  if (normalised.includes('recall')) return 'adRecall';
  if (normalised.includes('aware')) return 'awareness';
  if (normalised.includes('consider')) return 'consideration';
  if (normalised.includes('favour') || normalised.includes('favor')) return 'favourability';
  if (normalised.includes('intent')) return 'actionIntent';
  if (normalised.includes('custom')) return 'custom';
  return '';
}

export function unwrapEntity(payload) {
  if (!payload) return null;
  return payload.bls || payload.data || payload;
}

export function unwrapList(payload) {
  const data = payload?.data ?? payload?.bls ?? payload;
  return Array.isArray(data) ? data : [];
}
